import { CHANNEL_NAMES, ChatChannel, MAX_CHAT_LENGTH, MessageKind } from '@game/protocol';

import { collapsiblePanel, type PanelHeader } from './panel.ts';

/**
 * Registro de mensagens e campo de fala.
 *
 * O balão sobre o personagem some em segundos; este painel é a memória. Sem ele, quem
 * olhou para o outro lado da tela perde a fala do vizinho e o aviso de que subiu de
 * nível — e a única forma de recuperar seria pedir para repetirem.
 *
 * ## Um registro só, e não uma aba por canal
 *
 * Abas separadas parecem mais arrumadas e escondem justamente o que importa: a linha do
 * canal em que **não** se está olhando. Num jogo em que o aviso de "você foi convidado"
 * chega pelo mesmo painel que a conversa da guilda, a informação perdida atrás de uma aba
 * é informação que não chegou. O canal aparece como prefixo colorido, que é o bastante para
 * separar as conversas sem escondê-las.
 *
 * O canal de **envio** é o que o seletor diz, e ele é lembrado entre uma linha e outra:
 * quem está conversando na guilda quer que a segunda frase vá para o mesmo lugar que a
 * primeira. Prefixos digitados (`/g`, `/w`, `/p nome`) mudam o seletor, que é como a maior
 * parte das pessoas prefere fazer isso.
 */

export interface ChatHandlers {
  onSay(channel: ChatChannel, to: string, text: string): void;
}

/** Prefixos digitados que trocam de canal. `/p` ainda consome o nome do destinatário. */
const COMMANDS: ReadonlyArray<{ prefix: string; channel: ChatChannel }> = [
  { prefix: '/g ', channel: ChatChannel.Guild },
  { prefix: '/w ', channel: ChatChannel.World },
  { prefix: '/c ', channel: ChatChannel.Trade },
  { prefix: '/l ', channel: ChatChannel.Local },
  { prefix: '/p ', channel: ChatChannel.Private },
];

const CHANNEL_CLASS: Readonly<Record<ChatChannel, string>> = {
  [ChatChannel.Local]: 'speech',
  [ChatChannel.World]: 'channel-world',
  [ChatChannel.Trade]: 'channel-trade',
  [ChatChannel.Guild]: 'channel-guild',
  [ChatChannel.Private]: 'channel-private',
};

/**
 * Teto de linhas guardadas. Acima disso, as mais antigas saem do DOM.
 *
 * Subiu de 80 quando o combate passou a escrever aqui (ver `addHit`): uma briga escreve
 * uma linha por golpe nas duas direções, e com o teto antigo meio minuto de luta empurrava
 * para fora do registro tudo que tinha sido dito antes dela — que é exatamente o histórico
 * que este painel existe para guardar.
 */
const MAX_LINES = 200;

const KIND_CLASS: Readonly<Record<MessageKind, string>> = {
  [MessageKind.Info]: 'info',
  [MessageKind.Warn]: 'warn',
  [MessageKind.Advance]: 'advance',
  [MessageKind.Loss]: 'loss',
};

/**
 * `09/08 14:32:07` — dia, mês e hora do relógio de quem está jogando.
 *
 * Dia e mês sem o ano, e não a data por extenso: a linha tem 11 px de altura num painel
 * estreito, e o ano é a parte da data que nunca muda dentro de uma sessão. O dia fica
 * porque quem deixa o jogo aberto a noite inteira precisa distinguir a briga de ontem da
 * de hoje — sem ele, `03:14` de duas madrugadas diferentes é a mesma linha.
 */
function formatStamp(at: Date): string {
  const pad = (value: number): string => String(value).padStart(2, '0');
  return (
    `${pad(at.getDate())}/${pad(at.getMonth() + 1)} ` +
    `${pad(at.getHours())}:${pad(at.getMinutes())}:${pad(at.getSeconds())}`
  );
}

export class ChatPanel {
  private readonly log: HTMLElement;
  private readonly field: HTMLInputElement;
  private readonly picker: HTMLSelectElement;
  private readonly header: PanelHeader;
  /** Destinatário lembrado do último `/p nome`, para as respostas seguintes. */
  private whisperTo = '';

  constructor(root: HTMLElement, handlers: ChatHandlers) {
    root.classList.add('chat');
    root.innerHTML = '';

    // O painel mais caro da tela em altura: 154 px de uma janela que o mundo divide com ele.
    // Recolhido, o registro e o campo somem e a linha da grade encolhe para a barra de título
    // — são 130 px que voltam para o cenário (ver `#interface` em `style.css`). Quem joga
    // sozinho recolhe e esquece; o Enter continua funcionando, ver o `keydown` mais abaixo.
    this.header = collapsiblePanel(root, 'chat', 'Chat', {
      // O registro continuou recebendo linhas enquanto estava escondido, e a rolagem de um
      // elemento em `display: none` mede zero — sem isto, quem expande cai no meio do
      // histórico em vez de na última fala.
      onToggle: (collapsed) => {
        if (!collapsed) this.log.scrollTop = this.log.scrollHeight;
      },
    });

    this.log = document.createElement('div');
    this.log.className = 'chat-log';

    const form = document.createElement('form');
    form.className = 'chat-form';

    this.picker = document.createElement('select');
    this.picker.className = 'chat-channel';
    for (const channel of [
      ChatChannel.Local,
      ChatChannel.World,
      ChatChannel.Trade,
      ChatChannel.Guild,
      ChatChannel.Private,
    ]) {
      const option = document.createElement('option');
      option.value = String(channel);
      option.textContent = CHANNEL_NAMES[channel];
      this.picker.append(option);
    }

    this.field = document.createElement('input');
    this.field.type = 'text';
    this.field.placeholder = 'Enter para falar · /g guilda · /w mundo · /p nome';
    this.field.maxLength = MAX_CHAT_LENGTH;
    this.field.autocomplete = 'off';

    form.append(this.picker, this.field);
    root.append(this.header.root, this.log, form);

    form.addEventListener('submit', (event) => {
      event.preventDefault();

      const raw = this.field.value.trim();
      this.field.value = '';
      // Devolver o foco ao jogo é o passo que costuma ser esquecido: sem isto, as
      // setas do teclado continuariam mexendo o cursor no campo em vez de andar.
      this.field.blur();

      if (raw.length === 0) return;

      const parsed = this.parse(raw);
      if (parsed.text.length === 0) return;

      if (parsed.channel === ChatChannel.Private && parsed.to.length === 0) {
        this.addSystem('diga para quem: /p nome mensagem');
        return;
      }

      handlers.onSay(parsed.channel, parsed.to, parsed.text);
    });

    window.addEventListener('keydown', (event) => {
      if (event.key !== 'Enter' || document.activeElement === this.field) return;

      event.preventDefault();

      // Enter é o gesto de falar, e falar precisa funcionar com o painel recolhido — senão o
      // chat vira uma armadilha: o foco não pega num campo em `display: none`, `isTyping`
      // continua falso, e as letras que a pessoa digitou saem andando com o personagem.
      if (this.header.collapsed) this.header.toggle();

      this.field.focus();
    });

    this.field.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape') return;
      this.field.value = '';
      this.field.blur();
    });
  }

  /** `true` enquanto o jogador está digitando — a cena ignora movimento nesse período. */
  get isTyping(): boolean {
    return document.activeElement === this.field;
  }

  /**
   * Lê o prefixo digitado e devolve canal, destinatário e texto.
   *
   * O prefixo **muda o seletor**, e não só a linha que está sendo enviada. É a expectativa
   * de quem digita `/g`: a conversa mudou de lugar, e a próxima frase vai junto — ter que
   * repetir o prefixo em cada linha é o atrito que faz as pessoas desistirem do canal.
   */
  private parse(raw: string): { channel: ChatChannel; to: string; text: string } {
    for (const command of COMMANDS) {
      if (!raw.toLowerCase().startsWith(command.prefix)) continue;

      const rest = raw.slice(command.prefix.length).trim();
      this.picker.value = String(command.channel);

      if (command.channel !== ChatChannel.Private) {
        return { channel: command.channel, to: '', text: rest };
      }

      // `/p nome mensagem`: o nome é a primeira palavra, e fica lembrado para as
      // respostas seguintes — que é como uma conversa privada de verdade funciona.
      const space = rest.indexOf(' ');
      if (space === -1) {
        this.whisperTo = rest;
        return { channel: ChatChannel.Private, to: rest, text: '' };
      }

      this.whisperTo = rest.slice(0, space);
      return {
        channel: ChatChannel.Private,
        to: this.whisperTo,
        text: rest.slice(space + 1).trim(),
      };
    }

    const channel = Number(this.picker.value) as ChatChannel;
    return {
      channel,
      to: channel === ChatChannel.Private ? this.whisperTo : '',
      text: raw,
    };
  }

  addSpeech(channel: ChatChannel, name: string, text: string): void {
    const prefix = channel === ChatChannel.Local ? '' : `[${CHANNEL_NAMES[channel]}] `;
    this.append(CHANNEL_CLASS[channel] ?? 'speech', `${prefix}${name}: ${text}`);
  }

  addServerMessage(kind: MessageKind, text: string): void {
    this.append(KIND_CLASS[kind] ?? 'info', text);
  }

  addSystem(text: string): void {
    this.append('system', text);
  }

  /**
   * Um golpe dado ou levado, com a hora em que aconteceu.
   *
   * O número flutuante sobre a criatura dura menos de um segundo e não sobrevive a uma
   * briga com três lobos — some no meio da confusão que é justamente quando se quer saber
   * quanto cada um está tirando. A linha aqui é a memória disso, e é o que permite olhar
   * depois e responder "de onde veio o dano que me matou".
   *
   * A marca de tempo só aparece nestas linhas, e não em toda fala: aqui ela responde uma
   * pergunta que se faz de verdade — "isso foi agora ou faz um minuto?" —, enquanto numa
   * conversa em andamento ela seria uma coluna de números repetida em cada frase.
   */
  addHit(taken: boolean, text: string): void {
    this.append(taken ? 'hit-taken' : 'hit-dealt', text, new Date());
  }

  /**
   * `at` presente carimba a linha com data e hora.
   *
   * O texto entra por `append(string)`, que cria um nó de texto — e não por `innerHTML`:
   * nome de jogador é conteúdo de terceiro, e concatená-lo em marcação seria injeção.
   */
  private append(variant: string, text: string, at?: Date): void {
    const line = document.createElement('div');
    line.className = `chat-line ${variant}`;

    if (at) {
      const stamp = document.createElement('span');
      stamp.className = 'chat-time';
      stamp.textContent = formatStamp(at);
      line.append(stamp);
    }

    line.append(text);

    this.log.append(line);

    while (this.log.childElementCount > MAX_LINES) {
      this.log.firstElementChild?.remove();
    }

    // Rolagem só quando já se estava no fim: puxar a rolagem de quem está lendo o
    // histórico é a forma mais rápida de tornar o histórico inútil.
    const atBottom =
      this.log.scrollHeight - this.log.scrollTop - this.log.clientHeight < 40;
    if (atBottom) this.log.scrollTop = this.log.scrollHeight;
  }
}
